import { apiClient } from '@/lib/api/client';
import type { Order, OrderStatus } from '@/lib/types';
import type { OrderFilters, OrderTimelineResponse } from '../types/orders.types';

interface ApiData<T> {
  data: T;
}

interface OrderListResponse {
  data: Order[];
  activePage: number;
  totalPage: number;
}

export interface PaginatedOrders {
  orders: Order[];
  page: number;
  totalPages: number;
  total: number;
}

export const ordersService = {
  async getOrders(filters: OrderFilters = {}): Promise<PaginatedOrders> {
    const params = new URLSearchParams();
    // page and limit are required by the server
    const page = filters.page || 1;
    const limit = filters.limit || 10;
    params.set('page', String(page));
    params.set('limit', String(limit));
    if (filters.search) params.set('search', filters.search);

    // Build URL with filters array (like mobile-pro)
    let url = `/order/list/shops?${params.toString()}`;

    // Add filters as repeated params (e.g., filters=status1&filters=status2)
    if (filters.status) {
      // Support both single status and array of statuses
      const statusArray = Array.isArray(filters.status) ? filters.status : [filters.status];
      statusArray.forEach(status => {
        url += `&filters=${encodeURIComponent(status)}`;
      });
    }

    const response = await apiClient.get<ApiData<OrderListResponse>>(url);

    const data = response.data?.data;
    return {
      orders: data?.data ?? [],
      page: data?.activePage ?? page,
      totalPages: data?.totalPage ?? 1,
      total: (data?.totalPage ?? 1) * limit,
    };
  },

  async getOrder(id: string): Promise<Order | null> {
    const response = await apiClient.get<ApiData<Order>>('/order/info', {
      params: { orderId: id },
    });
    return response.data?.data ?? null;
  },

  async updateOrderStatus(id: string, action: OrderStatus): Promise<Order | null> {
    const response = await apiClient.put<ApiData<Order>>('/order/update/action', {
      orderId: id,
      action
    });
    return response.data?.data ?? null;
  },

  // Remboursement vendeur — même endpoint que client/admin (rôle SELLER
  // autorisé avec contrôle de propriété côté serveur). Le body est déjà
  // normalisé (`reason=requested_by_seller`) par `buildSellerRefundPayload`.
  async refundOrder(
    orderId: string,
    body: { amount: number; reason: string; comment?: string }
  ): Promise<Order | null> {
    const response = await apiClient.post<ApiData<Order>>(
      `/payment/refund/order/${orderId}`,
      body
    );
    return response.data?.data ?? null;
  },

  async getOrderTimeline(id: string): Promise<OrderTimelineResponse | null> {
    const response = await apiClient.get<ApiData<OrderTimelineResponse>>(`/order-tracking/timeline/${id}`);
    return response.data?.data ?? null;
  },
};
